const SESSION_ID_KEY = "ADMIN_SESSION_ID";
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
const TOKEN_CHECK_INTERVAL_MS = 60 * 1000;

let inactivityTimer = null;
let heartbeatTimer = null;
let tokenCheckTimer = null;
let listenersBound = false;
let logoutHandler = null;
let sessionExpiredHandling = false;

function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch (e) {
    return null;
  }
}

function isTokenExpired(token) {
  const expiry = getTokenExpiry(token);
  return expiry ? Date.now() >= expiry : false;
}

function hasValidToken() {
  const token = localStorage.getItem("apollo-done-token");
  return token && !isTokenExpired(token);
}

function getSessionId() {
  return localStorage.getItem(SESSION_ID_KEY);
}

function getSessionIdFromToken() {
  const token = localStorage.getItem("apollo-done-token");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.session_id || null;
  } catch (e) {
    return null;
  }
}

function resolveSessionId() {
  return getSessionIdFromToken() || getSessionId();
}

function setSessionId(sessionId) {
  if (sessionId) {
    localStorage.setItem(SESSION_ID_KEY, sessionId);
  } else {
    localStorage.removeItem(SESSION_ID_KEY);
  }
}

function clearLocalAuth() {
  localStorage.removeItem("apollo-done-token");
  localStorage.removeItem("DONE_USER");
  localStorage.removeItem("DONE_USER_ROLE");
  localStorage.removeItem("DONE_USER_PERMISSIONS");
  localStorage.removeItem("DONE_USER_ROLE_PERMISSIONS");
  localStorage.removeItem(SESSION_ID_KEY);
}

async function sendActivityPing() {
  if (!hasValidToken()) return;

  const sessionId = resolveSessionId();
  if (!sessionId) return;

  const tokenSessionId = getSessionIdFromToken();
  if (tokenSessionId && tokenSessionId !== getSessionId()) {
    setSessionId(tokenSessionId);
  }

  try {
    const { apolloClient } = await import("@/vue-apollo.js");
    await apolloClient.mutate({
      mutation: require("@/admin-login-session/api/adminSessionActivity.graphql"),
      variables: { sessionId },
      errorPolicy: "none",
    });
  } catch (e) {
    // Heartbeat is best-effort only. Do not log the user out here.
    // Session invalidation is handled on real API calls via auth errors.
  }
}

async function recordLogout(logoutType) {
  const sessionId = resolveSessionId();
  if (!sessionId) return;
  try {
    const { apolloClient } = await import("@/vue-apollo.js");
    await apolloClient.mutate({
      mutation: require("@/admin-login-session/api/adminSessionLogout.graphql"),
      variables: { sessionId, logoutType },
    });
  } catch (e) {
    // Logout recording is best-effort during auto-logout.
  }
}

async function handleSessionExpired(router, logoutType = "TOKEN_EXPIRED") {
  if (sessionExpiredHandling) return;
  sessionExpiredHandling = true;

  stopSessionManager();
  if (hasValidToken()) {
    await recordLogout(logoutType);
  }

  const { onLogout, apolloClient } = await import("@/vue-apollo.js");
  clearLocalAuth();
  await onLogout(apolloClient);

  if (router) {
    router.replace({ path: "/" });
  } else if (window.location.pathname !== "/") {
    window.location.href = "/";
  }

  sessionExpiredHandling = false;
}

async function performAutoLogout(logoutType, router) {
  await handleSessionExpired(router, logoutType);
}

function resetInactivityTimer(router) {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  inactivityTimer = setTimeout(() => {
    performAutoLogout("AUTO_INACTIVITY", router);
  }, INACTIVITY_TIMEOUT_MS);
}

function onUserActivity() {
  if (logoutHandler && logoutHandler.router) {
    resetInactivityTimer(logoutHandler.router);
  }
}

function bindActivityListeners() {
  if (listenersBound) return;
  const events = ["mousedown", "keydown", "scroll", "touchstart"];
  events.forEach((event) => window.addEventListener(event, onUserActivity, true));
  listenersBound = true;
}

function unbindActivityListeners() {
  if (!listenersBound) return;
  const events = ["mousedown", "keydown", "scroll", "touchstart"];
  events.forEach((event) => window.removeEventListener(event, onUserActivity, true));
  listenersBound = false;
}

function checkTokenExpiry(router) {
  const token = localStorage.getItem("apollo-done-token");
  if (!token) return;
  if (isTokenExpired(token)) {
    handleSessionExpired(router, "TOKEN_EXPIRED");
  }
}

export function startSessionManager(router) {
  const token = localStorage.getItem("apollo-done-token");
  if (!token) return;

  if (isTokenExpired(token)) {
    handleSessionExpired(router, "TOKEN_EXPIRED");
    return;
  }

  stopSessionManager();
  logoutHandler = { router };
  bindActivityListeners();
  resetInactivityTimer(router);

  sendActivityPing();
  heartbeatTimer = setInterval(sendActivityPing, HEARTBEAT_INTERVAL_MS);
  tokenCheckTimer = setInterval(() => checkTokenExpiry(router), TOKEN_CHECK_INTERVAL_MS);
}

export function stopSessionManager() {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  if (tokenCheckTimer) clearInterval(tokenCheckTimer);
  inactivityTimer = null;
  heartbeatTimer = null;
  tokenCheckTimer = null;
  logoutHandler = null;
  unbindActivityListeners();
}

export async function logoutAdminSession(logoutType = "MANUAL") {
  await recordLogout(logoutType);
  setSessionId(null);
  stopSessionManager();
}

export {
  setSessionId,
  getSessionId,
  isTokenExpired,
  getTokenExpiry,
  clearLocalAuth,
  hasValidToken,
  handleSessionExpired,
};
